import { createClient as createSupabaseClient } from '@supabase/supabase-js';

// ─── Singleton instance ───────────────────────────────────────────────────────
// One client, created once, reused everywhere. Never call createClient() in a
// loop or on every render — import the `supabase` export directly instead.
let _instance: ReturnType<typeof createSupabaseClient> | null = null;

export function createClient() {
  if (_instance) return _instance;

  _instance = createSupabaseClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        // Store session in localStorage — simple, reliable, no cookie issues
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        persistSession: true,
        // autoRefreshToken must be true so that Supabase Realtime WebSocket
        // channels keep a valid auth token and stay connected indefinitely.
        // Without this, tokens expire and realtime stops working until the
        // page is reloaded or the user navigates away and back.
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    }
  );

  return _instance;
}

// Convenience export — use this in components instead of calling createClient()
export const supabase = createClient();

// ─── Session helpers ──────────────────────────────────────────────────────────

/** Remove all Supabase auth keys from localStorage */
export const clearSupabaseSession = () => {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('sb-') || k.includes('supabase') || k.includes('auth'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
};

/** Clear session and redirect to login */
export const invalidateSession = (() => {
  let inProgress = false;
  return () => {
    if (inProgress) return;
    inProgress = true;
    clearSupabaseSession();
    // Also sign out from Supabase to invalidate server-side session
    supabase.auth.signOut().catch(() => {}).finally(() => {
      inProgress = false;
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    });
  };
})();
